import React, { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useLocalisedConstantsContext } from '@futrli/localisation/core/localised-constants-context';

import { UILoader } from '@futrli/ui-components';
import { workspaceByIdSelector } from '@futrli/shared-services';
import { WorkspacesListingItem } from '@futrli-sdk/workspaces';

import { usePredictionsInfoContext } from '../../context/predictions-info-context';
import { StoreState, VIEW_TYPES } from '../../../../core/types';
import { UserGridSettingsContext } from '../../reports/user-grid-settings/user-grid-settings-context';
import { getColumnsWithFYInfo } from '../utils/forecast-columns';
import { PredictionsTotalsPageWrapper } from '../predictions-totals-page-wrapper';
import { PredictManagementContext } from '../../../../predict-management/context/predict-management-context';
import { getAccountRowsWithFYInfo } from '../../reports/data-grid/format-rows';
import { getProfitAndLossConsolidatedAccountCategories } from '../util-fns/consolidation-util-fns';
import { REPORT_SECTION } from '../../../../core/constants';
import { useConsolidationDataContext } from '../../../../predict-management/context/consolidation-data-context';
import { ForecastUpdating } from '../../components/forecast-updating';

export const ConsolidationProfitAndLoss = () => {
  const { workspaceId: tempWorkspaceId } = useParams();
  const workspaceId = tempWorkspaceId || '';

  const intl = useIntl();

  const {
    consolidatedForecastReport,
    forecast,
    isLoading,
    periodType,
    totalCashPredictRow,
    viewType
  } = usePredictionsInfoContext();

  const { financialConfig } = useContext(PredictManagementContext);

  const { nodeStaticIds } = useLocalisedConstantsContext();

  const workspace = useSelector((state: StoreState) => workspaceByIdSelector(state.normalised, workspaceId));

  const { consolidation } = useConsolidationDataContext();

  const { expandedRows, getShowZeroValueAccounts } = useContext(UserGridSettingsContext);

  const columns = useMemo(() => {
    if (!consolidatedForecastReport) {
      return [];
    }
    if (viewType === VIEW_TYPES.CASH_FLOW) {
      return consolidatedForecastReport.data.cash_flow_columns;
    }
    if (financialConfig && periodType === 'MONTHLY') {
      return getColumnsWithFYInfo(consolidatedForecastReport.data.columns, financialConfig);
    } else {
      return consolidatedForecastReport.data.columns;
    }
  }, [consolidatedForecastReport, financialConfig, periodType, viewType]);

  const dataGridRows = useMemo(() => {
    let rows = getProfitAndLossConsolidatedAccountCategories(
      intl,
      nodeStaticIds,
      workspace as WorkspacesListingItem,
      viewType === VIEW_TYPES.CASH_FLOW ? REPORT_SECTION.CashFlow : REPORT_SECTION.ProfitLoss,
      expandedRows,
      consolidatedForecastReport,
      consolidation,
      false,
      getShowZeroValueAccounts(forecast?.id || '')
    );

    if (financialConfig && periodType === 'MONTHLY' && rows) {
      rows = getAccountRowsWithFYInfo(rows, columns);
    }

    return rows;
  }, [
    intl,
    nodeStaticIds,
    consolidation,
    consolidatedForecastReport,
    expandedRows,
    viewType,
    workspace,
    financialConfig,
    periodType,
    columns,
    getShowZeroValueAccounts,
    forecast?.id
  ]);

  if (!consolidatedForecastReport && isLoading) {
    return <UILoader isFull />;
  }

  return (
    <>
      <PredictionsTotalsPageWrapper
        columns={columns}
        totalAccountRows={dataGridRows}
        totalCashPredictRow={totalCashPredictRow}
      />
      <ForecastUpdating show={!!isLoading} />
    </>
  );
};
